import React from "react";
import { useQuery } from "@apollo/client";
import { CardSection } from "../../graphql/schema";
import { Grid } from "../common/grid/Grid";
import { PageSection } from "../common/pageSection/PageSection";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  shorthands,
  Divider,
} from "@fluentui/react-components";
import { GET_CARDS } from "../../graphql/queries";
import { useCmsLocale } from "../../i18n/cmsLocale";
import {
  ScalesRegular,
  ShieldLockRegular,
  HandDrawRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  backgroundColor: {
    backgroundColor: "#172433",
  },
  content: {
    marginTop: tokens.spacingVerticalXXXL,
    marginBottom: tokens.spacingVerticalXXXL,
  },
  itemWrapper: {
    display: "flex",
    height: "auto",
  },
  fourTwelfth: {
    ...shorthands.gridArea("auto", "auto", "auto", "span 12"),

    "@media screen and (min-width: 1007px)": {
      ...shorthands.gridArea("auto", "auto", "auto", "span 4"),
    },
  },
  tewlweTwelfth: {
    ...shorthands.gridArea("auto", "auto", "auto", "span 12"),
  },
  keyHighlightItem: {
    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    color: tokens.colorBrandBackground,
    backgroundColor: "#C9E3FF",

    borderTopRightRadius: "20px",
    borderTopLeftRadius: "20px",
    borderBottomRightRadius: "20px",
    borderBottomLeftRadius: "20px",

    ...shorthands.padding(tokens.spacingHorizontalXL),
        "@media screen and (max-width: 1007px)": {
          width: "80%",
          ...shorthands.margin(0, "auto"),
        },
  },
  keyHighlightText: {
    display: "flex",
    flexDirection: "column",
    rowGap: tokens.spacingVerticalM,
  },
  icon: {
    height: "125px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      width: "100%",
      height: "100%",
    },
  },
  divider: {
    display: "block",
    flexGrow: 0,
    width: "45%",
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalL,
    "::before": {
      ...shorthands.borderRadius("1000px"),
      backgroundColor: tokens.colorBrandBackground,
      height: "3px",
      ...shorthands.border(0),
    },
    "::after": { ...shorthands.border(0) },
  },
  scaleIcon: {
    width: "99px",
    height: "146px",
  },
  shieldIcon: {
    width: "113px",
    height: "146px",
  },
  drawIcon: {
    width: "100px",
    height: "146px",
  },
});
export const KeyHighlights = () => {
  const classes = useStyles();
  const locale = useCmsLocale();
  const { data, error, loading } = useQuery(GET_CARDS, { variables: { locale } });
  if (loading || error || !data.cardSection) return <></>;

  const cardSection: CardSection = data.cardSection;
  type CardIconType = "scale" | "shield" | "draw";
  const getIcon = (icon: CardIconType) => {
    switch (icon) {
      case "scale":
        return (
          <ScalesRegular
            className={mergeClasses(classes.icon, classes.scaleIcon)}
          />
        );
      case "shield":
        return (
          <ShieldLockRegular
            className={mergeClasses(classes.icon, classes.shieldIcon)}
          />
        );
      case "draw":
        return (
          <HandDrawRegular
            className={mergeClasses(classes.icon, classes.drawIcon)}
          />
        );
    }
  };

  return (
    <div className={classes.backgroundColor}>
      <PageSection>
        <Grid className={classes.content}>
          {cardSection.cards ? (
            cardSection.cards?.map((x, i) => (
              <div
                className={mergeClasses(classes.itemWrapper, classes.fourTwelfth)}
                key={i}
              >
                <div className={classes.keyHighlightItem}>
                  {getIcon(
                    x?.icon
                      ? (x.icon.toLowerCase() as CardIconType)
                      : "shield"
                  )}
                  <Divider className={classes.divider} />
                  <div className={classes.keyHighlightText}>
                    <Text size={500} align="center" weight="semibold">
                      {x?.title ?? ""}
                    </Text>
                    <Text size={300} align="center">
                      {x?.description ?? ""}
                    </Text>
                    <Text size={300} align="center">
                      {x?.descriptionPt2 ?? ""}
                    </Text>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <></>
          )}
        </Grid>
      </PageSection>
    </div>
  );
};
